<template>
  <div class="business-intelligence-manual-entry-row">
    <div v-if="!inEditMode" class="__content-container">
      <div class="__info-container">
        <div class="__header-container">
          <div class="__title">{{ data.supplierName }}</div>
          <div v-if="lotOptions.length" class="__lot">
            {{ lotTitle ? lotTitle : $t('page.business-intelligence.unknown-lot') }}
          </div>
          <div class="__notes">
            {{ data.notes ? data.notes : $t('page.business-intelligence.empty-notes') }}
          </div>
        </div>
        <div class="__amount number-monospace" v-if="isDisplayAmount">{{ displayAmount }}</div>
      </div>
      <div class="__actions-container">
        <BaseRoundButton icon="edit" isLight isTransparent @click.native="onEditClicked" />
        <BaseRoundButton icon="trash" isRed isTransparent @click.native="onDeleteClicked" />
      </div>
    </div>
    <div v-else class="__edit-mode-container">
      <div>
        <form click.prevent @keydown.enter.prevent>
          <FormInput
            ref="formSupplierName"
            is-required
            is-compact
            has-gray-background
            name="name"
            type="text"
            v-model="formSupplierName"
            v-validate="'required'"
            :placeholder="$t('page.business-intelligence.manage-dialog.form.supplier-name')"
            :error="supplierNameError"
            :data-vv-scope="data.id"
          />
          <FormInput
            ref="formAmount"
            v-if="isDisplayAmount"
            is-required
            is-compact
            has-gray-background
            name="amount"
            type="number"
            v-model="formAmount"
            v-validate="'required'"
            :placeholder="$t('page.business-intelligence.manage-dialog.form.amount')"
            :error="amountError"
            :data-vv-scope="data.id"
          />
          <BaseSelect
            v-if="lotOptions !== undefined && lotOptions.length > 0"
            name="lot"
            is-compact
            :value="formRelatedLot"
            :items="lotOptions"
            item-text="title"
            item-value="id"
            @change="onLotChanged"
          />
          <FormInput
            ref="formNotes"
            is-compact
            has-gray-background
            name="notes"
            type="text"
            v-model="formNotes"
            :placeholder="$t('page.business-intelligence.manage-dialog.form.notes')"
          />
          <div class="__edit-actions">
            <BaseButton isOutlined isFullWidth @click.native="onCancelClicked">
              {{ $t('actions.cancel') }}
            </BaseButton>
            <BaseButton isFullWidth :disabled="isFormInvalid" @click.native="onSaveClicked">
              {{ $t('actions.save') }}
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseSelect from '@/components/base/BaseSelect';
import FormInput from '@/components/form/FormInput.vue';
import { formatNumToCurrency } from '@/util/currency-util';

export default {
  name: 'ManualEntryRow',
  components: {
    BaseButton,
    BaseRoundButton,
    BaseSelect,
    FormInput,
  },
  props: {
    data: {
      type: Object, // can be a bid / award / potential bidder
      required: true,
    },
    isDisplayAmount: {
      type: Boolean,
      required: false,
      default: true,
    },
    reset: {
      // used to trigger reset of form state when parent component changes
      type: Boolean,
      required: false,
    },
    lotOptions: {
      type: Array,
      required: false,
      default: undefined,
    },
    isLotRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      inEditMode: false,
      formSupplierName: '',
      formAmount: 0,
      formNotes: '',
      formRelatedLot: null,
    };
  },
  computed: {
    displayAmount() {
      if (this.isDisplayAmount === false) {
        return null;
      }
      if (this.data.amount === undefined || this.data.amount === null) {
        return this.formatNumToCurrency(0);
      }
      return this.formatNumToCurrency(this.data.amount);
    },
    isFormInvalid() {
      return (
        this.errors.items.length > 0 ||
        this.formSupplierName === '' ||
        (this.isLotRequired &&
          this.lotOptions !== undefined &&
          this.lotOptions.length > 0 &&
          this.formRelatedLot === null)
      );
    },
    supplierNameError() {
      const formError = this.errors.items.find((x) => x.field === 'name' && x.scope === this.data.id);
      return formError ? formError.msg : undefined;
    },
    amountError() {
      const formError = this.errors.items.find((x) => x.field === 'amount' && x.scope === this.data.id);
      return formError ? formError.msg : undefined;
    },
    lotTitle() {
      const lot = this.lotOptions.find((lot) => lot.id === this.data.relatedLot);
      if (lot) {
        return lot.title;
      }
      return undefined;
    },
  },
  methods: {
    formatNumToCurrency,
    onEditClicked() {
      this.formSupplierName = this.data.supplierName;
      this.formAmount = this.data.amount || 0;
      this.formNotes = this.data.notes;
      this.formRelatedLot = this.data.relatedLot;

      this.inEditMode = true;

      this.$nextTick(() => {
        const refInput = this.$refs.formSupplierName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
            refInput.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'start' });
          });
        }
      });
    },
    onDeleteClicked() {
      this.$emit('on-delete', this.data.id);
    },
    onSaveClicked() {
      this.$emit('on-update', this.data.id, {
        supplierName: this.formSupplierName,
        ...(this.isDisplayAmount && { amount: this.formAmount }),
        notes: this.formNotes,
        relatedLot: this.formRelatedLot,
      });
    },
    onCancelClicked() {
      this.resetFormState();
    },
    onLotChanged(lot) {
      this.formRelatedLot = lot;
    },
    resetFormState() {
      this.inEditMode = false;

      this.formSupplierName = '';
      this.formAmount = 0;
      this.formNotes = '';
      this.formRelatedLot = null;
    },
  },
  watch: {
    reset() {
      this.resetFormState();
    },
  },
};
</script>

<style lang="scss">
.business-intelligence-manual-entry-row {
  padding: 8px 12px;
  margin: 8px 4px;
  border: 1px solid $color-border;
  border-radius: $border-radius-root;
  font-size: $font-size-small;
  color: $color-normal-pale;

  > .__content-container {
    display: flex;
    justify-content: space-between;
    gap: 8px;

    > .__info-container {
      display: flex;
      flex: auto 1;
      justify-content: space-between;
      min-width: 0; // critical for ellipsis
      flex-direction: row;
      gap: 4px;
      align-items: center;

      > .__header-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0; // also important

        > .__title {
          font-weight: 600;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        > .__notes {
          font-style: italic;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    > .__actions-container {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
    }
  }

  > .__edit-mode-container {
    .__edit-actions {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }
  }
}
</style>
